/**
 * AnexaGDPRGenerator - Anexă GDPR (Regulament UE 679/2016 + Legea 190/2018)
 * Acord de prelucrare a datelor cu caracter personal + informare persoană vizată.
 * Model standard pentru contracte de prestări servicii construcții — beneficiar
 * poate fi persoană fizică (Nume + CNP + adresă) sau juridică (Denumire + CUI +
 * sediu). Executantul = operator de date (art. 4 pct. 7 GDPR).
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, ShieldCheck } from "lucide-react";

function generateAnexaGDPRHTML(d, header) {
  const annexLabel = header?.annex_label || "ANEXA";
  const ctrNo = d.contract_number || "___";
  const clauzeExtra = (d.clauze_suplimentare || "").trim();
  const isPF = d.beneficiar_type === "pf";
  const perioada = d.perioada_stocare || "10 ani de la finalizarea contractului (obligații fiscal-contabile — Legea 82/1991)";
  const scopExtra = (d.scop_prelucrare_extra || "").trim();

  // Rând identificare beneficiar (persoană vizată)
  const identificareBenef = isPF
    ? `<tr>
        <td>Persoană vizată (Beneficiar - PF)</td>
        <td>
          <b>Nume și prenume:</b> ${d.beneficiar_name || "___"}<br>
          <b>CNP:</b> ${d.beneficiar_cnp || "___"}<br>
          <b>Adresa de domiciliu:</b> ${d.beneficiar_address || "___"}<br>
          <b>Telefon:</b> ${d.beneficiar_phone || "___"} · <b>Email:</b> ${d.beneficiar_email || "___"}<br>
          <b>Serie/Nr. CI:</b> ${d.beneficiar_ci || "___"}
        </td>
      </tr>`
    : `<tr>
        <td>Persoană vizată (Beneficiar - PJ)</td>
        <td>
          <b>Denumire:</b> ${d.beneficiar_name || "___"}<br>
          <b>CUI:</b> ${d.beneficiar_cui || "___"} · <b>Reg. Com.:</b> ${d.beneficiar_reg_com || "___"}<br>
          <b>Sediu social:</b> ${d.beneficiar_address || "___"}<br>
          <b>Reprezentant legal:</b> ${d.beneficiar_reprezentant || "___"} — ${d.beneficiar_functie || "reprezentant legal"}<br>
          <b>Telefon:</b> ${d.beneficiar_phone || "___"} · <b>Email:</b> ${d.beneficiar_email || "___"}
        </td>
      </tr>`;

  // Categorii de date prelucrate
  const categoriiPF = `
    <li><b>Date de identificare:</b> nume, prenume, CNP, serie/nr. carte de identitate;</li>
    <li><b>Date de contact:</b> adresă de domiciliu / corespondență, număr de telefon, adresă e-mail;</li>
    <li><b>Date financiare:</b> cont bancar (IBAN), sume facturate, sume plătite;</li>
    <li><b>Date privind executarea contractului:</b> adresa punctului de lucru, semnătură, corespondență contractuală.</li>`;
  const categoriiPJ = `
    <li><b>Date de identificare a firmei:</b> denumire, CUI, Nr. Reg. Com., sediu social, cod CAEN;</li>
    <li><b>Date reprezentant legal:</b> nume, prenume, funcție, semnătură;</li>
    <li><b>Date de contact:</b> telefon, e-mail, adresă corespondență;</li>
    <li><b>Date financiare:</b> cont bancar (IBAN), sume facturate, sume plătite;</li>
    <li><b>Date privind executarea contractului:</b> adresa punctului de lucru, corespondență contractuală.</li>`;

  return `<!DOCTYPE html><html lang="ro"><head><meta charset="UTF-8"><style>
    body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;padding:20px;color:#1e293b;line-height:1.65;font-size:12.5px}
    h1{text-align:center;color:#4338ca;font-size:17px;margin-bottom:4px;text-transform:uppercase}
    h2{text-align:center;color:#4f46e5;font-size:13px;font-weight:normal;margin-top:0}
    .header{border-bottom:3px solid #6366f1;padding-bottom:15px;margin-bottom:20px}
    .section{margin-bottom:14px}
    .section-title{font-weight:bold;color:#4338ca;border-bottom:1px solid #c7d2fe;padding-bottom:4px;margin-bottom:8px;font-size:13px}
    table{width:100%;border-collapse:collapse;margin:8px 0}
    td{padding:6px 10px;border:1px solid #d1d5db;vertical-align:top}
    td:first-child{width:32%;background:#eef2ff;font-weight:600;color:#3730a3}
    ol, ul{margin:6px 0;padding-left:22px}
    ol li, ul li{margin-bottom:5px;text-align:justify}
    .signatures{display:flex;justify-content:space-between;margin-top:40px;page-break-inside:avoid}
    .sig-box{width:45%;text-align:center;border-top:2px solid #6366f1;padding-top:10px}
    .sig-box p{margin:4px 0;font-size:12px}
    .checkbox{display:inline-block;width:14px;height:14px;border:1.5px solid #4338ca;vertical-align:middle;margin-right:6px}
    .footer{text-align:center;margin-top:30px;font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:10px}
    .legal{background:#eef2ff;border:1px solid #c7d2fe;border-radius:6px;padding:8px 12px;font-size:11.5px;color:#312e81}
    .consimtamant{background:#fef3c7;border:1.5px solid #fbbf24;border-radius:6px;padding:12px;margin-top:15px}
  </style></head><body>
  <div class="header">
    <h1>Acord privind prelucrarea datelor cu caracter personal (GDPR)</h1>
    <h2>${annexLabel} CTR ${ctrNo} — anexă și parte integrantă la Contractul nr. ${ctrNo} din ${d.contract_date || "___"}</h2>
  </div>

  <div class="section">
    <div class="section-title">I. Părțile</div>
    <table>
      <tr>
        <td>Operator de date cu caracter personal (Executant)</td>
        <td>
          <b>${d.executant_name || "___"}</b>, CUI: ${d.executant_cui || "___"},<br>
          Sediu: ${d.executant_address || "___"}<br>
          Nr. Reg. Com.: ${d.executant_reg_com || "___"}<br>
          Reprezentant: ${d.executant_reprezentant || "___"} (${d.executant_functie || "Administrator"})<br>
          <b>Contact GDPR:</b> ${d.executant_gdpr_email || d.executant_email || "___"} · Tel: ${d.executant_phone || "___"}
        </td>
      </tr>
      ${identificareBenef}
    </table>
  </div>

  <div class="section">
    <div class="section-title">II. Temei legal</div>
    <div class="legal">
      • <b>Regulamentul (UE) 2016/679 (GDPR)</b> — art. 6 alin. (1) lit. (b) [executarea contractului], lit. (c) [obligație legală: facturare, contabilitate], lit. (a) [consimțământ, unde aplicabil]<br>
      • <b>Legea nr. 190/2018</b> privind măsurile de punere în aplicare a GDPR<br>
      • <b>Legea nr. 82/1991</b> a contabilității (obligație de păstrare a documentelor 10 ani)<br>
      • <b>Codul Fiscal</b> (Legea 227/2015) — obligații fiscale
    </div>
  </div>

  <div class="section">
    <div class="section-title">III. Categorii de date prelucrate</div>
    <p>Operatorul prelucrează următoarele categorii de date cu caracter personal ale Persoanei vizate:</p>
    <ul>${isPF ? categoriiPF : categoriiPJ}</ul>
  </div>

  <div class="section">
    <div class="section-title">IV. Scopul prelucrării</div>
    <ol>
      <li><b>Executarea contractului</b> nr. ${ctrNo} din ${d.contract_date || "___"} având ca obiect <b>${d.obiect_lucrare || "___"}</b> la <b>${d.locatie_lucrare || "___"}</b>;</li>
      <li><b>Emiterea facturilor</b> și evidența financiar-contabilă (obligație legală);</li>
      <li><b>Comunicare</b> pe parcursul derulării contractului (telefon, e-mail, corespondență);</li>
      <li><b>Îndeplinirea obligațiilor legale</b> de raportare fiscală (ANAF, e-Factura) și declarative;</li>
      <li><b>Recuperarea creanțelor</b>, dacă este cazul, inclusiv prin instanțele judecătorești;</li>
      <li><b>Arhivare</b> conform Legii contabilității nr. 82/1991.</li>
      ${scopExtra ? `<li><b>Scop suplimentar:</b> ${scopExtra}</li>` : ""}
    </ol>
  </div>

  <div class="section">
    <div class="section-title">V. Destinatarii datelor</div>
    <p>Datele pot fi comunicate, strict pentru scopurile menționate mai sus, către:</p>
    <ul>
      <li>Contabilul / firma de contabilitate care asigură evidența financiar-contabilă a Operatorului;</li>
      <li>Autorități publice (ANAF, ITM, Inspectoratul de Stat în Construcții, Registrul Comerțului, Curte / Parchet) — la cererea acestora sau în îndeplinirea obligațiilor legale;</li>
      <li>Furnizori de servicii IT (găzduire, cloud, e-mail) care asigură infrastructura digitală a Operatorului, sub obligații stricte de confidențialitate;</li>
      <li>Furnizori de servicii de curierat / poștă pentru transmiterea documentelor fizice;</li>
      <li>Bănci și instituții financiare (pentru încasările contractuale);</li>
      <li>Consultanți juridici, în situația litigiilor.</li>
    </ul>
    <p><b>Datele NU sunt transferate</b> în afara Spațiului Economic European (SEE), cu excepția cazurilor în care furnizorii de cloud impun asemenea transfer, situație în care se aplică clauze contractuale standard aprobate de Comisia Europeană.</p>
  </div>

  <div class="section">
    <div class="section-title">VI. Perioada de stocare</div>
    <p><b>${perioada}</b>. După expirarea acestei perioade, datele sunt șterse sau anonimizate în mod ireversibil.</p>
  </div>

  <div class="section">
    <div class="section-title">VII. Drepturile Persoanei vizate</div>
    <p>Conform art. 15–22 din GDPR, Persoana vizată are următoarele drepturi:</p>
    <ol>
      <li><b>Dreptul de acces</b> — de a obține confirmarea prelucrării datelor și o copie a acestora;</li>
      <li><b>Dreptul la rectificare</b> — de a solicita corectarea datelor inexacte;</li>
      <li><b>Dreptul la ștergere</b> ("dreptul de a fi uitat") — cu excepțiile prevăzute de lege (ex. obligații fiscal-contabile);</li>
      <li><b>Dreptul la restricționarea prelucrării</b>;</li>
      <li><b>Dreptul la portabilitatea datelor</b> — de a primi datele într-un format structurat, utilizat curent;</li>
      <li><b>Dreptul de a se opune</b> prelucrării, în anumite condiții;</li>
      <li><b>Dreptul de a nu face obiectul unei decizii automate</b>, inclusiv profilare;</li>
      <li><b>Dreptul de a retrage consimțământul</b> oricând, fără a afecta legalitatea prelucrărilor efectuate anterior;</li>
      <li><b>Dreptul de a depune plângere</b> la <b>Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</b>, B-dul G-ral. Gheorghe Magheru 28-30, București, Sector 1, cod poștal 010336, tel: +40.318.059.211, e-mail: anspdcp@dataprotection.ro, sau la o instanță de judecată competentă.</li>
    </ol>
    <p>Exercitarea drepturilor se face prin cerere scrisă adresată Operatorului la datele de contact indicate la Cap. I. Operatorul va răspunde în maxim <b>30 de zile</b> de la primirea cererii.</p>
  </div>

  <div class="section">
    <div class="section-title">VIII. Măsuri de securitate</div>
    <p>Operatorul aplică măsuri tehnice și organizatorice adecvate pentru a proteja datele împotriva accesului neautorizat, pierderii, alterării sau divulgării: acces restricționat, autentificare cu parolă, backup periodic, criptare (unde este cazul), instruire personal.</p>
  </div>

  ${clauzeExtra ? `<div class="section">
    <div class="section-title">IX. Clauze suplimentare</div>
    <p style="text-align:justify;white-space:pre-wrap">${clauzeExtra}</p>
  </div>` : ""}

  <div class="consimtamant">
    <div style="font-weight:bold;color:#78350f;margin-bottom:8px;text-align:center;">CONSIMȚĂMÂNT EXPRES</div>
    <p><span class="checkbox"></span> Confirm că am citit prezenta informare și <b>îmi exprim consimțământul expres</b> pentru prelucrarea datelor mele cu caracter personal de către <b>${d.executant_name || "___"}</b>, în scopurile și în condițiile descrise mai sus.</p>
    <p><span class="checkbox"></span> Sunt de acord să fiu contactat pe adresa de e-mail și telefonul indicate mai sus pentru chestiuni legate strict de executarea contractului.</p>
    <p style="font-size:11px;color:#78350f;margin-top:8px;"><i>Bifarea căsuțelor este echivalentul juridic al semnăturii, conform art. 4 pct. 11 din GDPR.</i></p>
  </div>

  <div class="signatures">
    <div class="sig-box">
      <p><b>Persoană vizată (Beneficiar)</b></p>
      <p>${d.beneficiar_name || "___"}${!isPF && d.beneficiar_reprezentant ? `<br>prin ${d.beneficiar_reprezentant}` : ""}</p>
      <p style="margin-top:30px">Data: ${d.data_acord || "___"}<br>Semnătura: _______________</p>
    </div>
    <div class="sig-box">
      <p><b>Operator (Executant)</b></p>
      <p>${d.executant_name || "___"}<br>prin ${d.executant_reprezentant || "___"}<br>(${d.executant_functie || "Administrator"})</p>
      <p style="margin-top:30px">Data: ${d.data_acord || "___"}<br>Semnătura & Ștampila: _______________</p>
    </div>
  </div>

  <div class="footer">
    Document generat prin <a href="https://e-tesa.ro" style="color:#4338ca;text-decoration:none">e-Tesa</a> · Conform GDPR (Reg. UE 679/2016) și Legea 190/2018
  </div>
</body></html>`;
}

export default function AnexaGDPRGenerator({ contractData, existingAnnex, onSave, onCancel }) {
  const existing = existingAnnex?.data || {};

  const [form, setForm] = useState({
    contract_number: existing.contract_number || contractData.contract_number || "",
    contract_date: existing.contract_date || contractData.contract_date || "",
    // Executant / Operator
    executant_name: existing.executant_name || contractData.executant_name || "",
    executant_cui: existing.executant_cui || contractData.executant_cui || "",
    executant_reg_com: existing.executant_reg_com || contractData.executant_reg_com || "",
    executant_address: existing.executant_address || contractData.executant_address || "",
    executant_reprezentant: existing.executant_reprezentant || contractData.executant_reprezentant || "",
    executant_functie: existing.executant_functie || contractData.executant_functie || "Administrator",
    executant_email: existing.executant_email || contractData.executant_email || "",
    executant_gdpr_email: existing.executant_gdpr_email || "",
    executant_phone: existing.executant_phone || contractData.executant_telefon || "",
    // Beneficiar (persoană vizată)
    beneficiar_type: existing.beneficiar_type || (contractData.beneficiar_cui ? "pj" : "pf"),
    beneficiar_name: existing.beneficiar_name || contractData.beneficiar_name || "",
    beneficiar_cnp: existing.beneficiar_cnp || "",
    beneficiar_ci: existing.beneficiar_ci || "",
    beneficiar_cui: existing.beneficiar_cui || contractData.beneficiar_cui || "",
    beneficiar_reg_com: existing.beneficiar_reg_com || contractData.beneficiar_reg_com || "",
    beneficiar_address: existing.beneficiar_address || contractData.beneficiar_address || "",
    beneficiar_reprezentant: existing.beneficiar_reprezentant || contractData.beneficiar_reprezentant || "",
    beneficiar_functie: existing.beneficiar_functie || contractData.beneficiar_functie || "",
    beneficiar_phone: existing.beneficiar_phone || contractData.beneficiar_telefon || "",
    beneficiar_email: existing.beneficiar_email || contractData.beneficiar_email || "",
    // Contract
    obiect_lucrare: existing.obiect_lucrare || contractData.obiect_lucrare || "",
    locatie_lucrare: existing.locatie_lucrare || contractData.locatie_lucrare || "",
    // GDPR-specific
    perioada_stocare: existing.perioada_stocare || "10 ani de la finalizarea contractului (obligații fiscal-contabile — Legea 82/1991)",
    scop_prelucrare_extra: existing.scop_prelucrare_extra || "",
    clauze_suplimentare: existing.clauze_suplimentare || "",
    data_acord: existing.data_acord || new Date().toISOString().slice(0, 10),
  });

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const isPF = form.beneficiar_type === "pf";

  const handleSave = () => {
    const header = { annex_label: existingAnnex?.annex_label || "ANEXA" };
    const html = generateAnexaGDPRHTML(form, header);
    onSave({
      annex_type: "anexa_gdpr",
      data: form,
      html_content: html,
      title: "Acord GDPR",
    });
  };

  return (
    <div className="space-y-4" data-testid="anexa-gdpr-editor">
      <Card className="border-0 shadow-lg border-t-4 border-t-indigo-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel} className="p-1 h-auto" data-testid="cancel-anexa-gdpr-top"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                {existingAnnex?.annex_label || "ANEXA"} — Acord GDPR
              </CardTitle>
              <CardDescription>
                Regulament UE 679/2016 (GDPR) + Legea 190/2018. Conține toate elementele obligatorii: operator, categorii date, temei legal, scop, destinatari, perioadă, drepturile persoanei vizate, contact ANSPDCP.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tip beneficiar */}
          <div className="p-4 bg-indigo-50 rounded-lg">
            <Label className="text-indigo-800 font-semibold">Tipul beneficiarului (persoanei vizate)</Label>
            <Select value={form.beneficiar_type} onValueChange={(v) => upd("beneficiar_type", v)}>
              <SelectTrigger className="mt-2" data-testid="gdpr-beneficiar-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pf">Persoană Fizică (Nume + CNP + Adresă)</SelectItem>
                <SelectItem value="pj">Persoană Juridică (Denumire + CUI + Sediu)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-indigo-700 mt-2">
              Selectează după tipul beneficiarului — formularul se adaptează cu câmpurile corespunzătoare.
            </p>
          </div>

          {/* Operator (Executant) */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-slate-800">Operator de date (Executant)</h3>
            <div><Label>Denumire firmă</Label><Input value={form.executant_name} onChange={(e) => upd("executant_name", e.target.value)} data-testid="gdpr-executant-name" /></div>
            <div><Label>CUI</Label><Input value={form.executant_cui} onChange={(e) => upd("executant_cui", e.target.value)} /></div>
            <div><Label>Nr. Reg. Com.</Label><Input value={form.executant_reg_com} onChange={(e) => upd("executant_reg_com", e.target.value)} placeholder="J40/1234/2020" /></div>
            <div><Label>Reprezentant</Label><Input value={form.executant_reprezentant} onChange={(e) => upd("executant_reprezentant", e.target.value)} /></div>
            <div className="col-span-2"><Label>Sediu social</Label><Input value={form.executant_address} onChange={(e) => upd("executant_address", e.target.value)} /></div>
            <div><Label>Funcție reprezentant</Label><Input value={form.executant_functie} onChange={(e) => upd("executant_functie", e.target.value)} /></div>
            <div><Label>Telefon Operator</Label><Input value={form.executant_phone} onChange={(e) => upd("executant_phone", e.target.value)} /></div>
            <div className="col-span-2"><Label>E-mail contact GDPR (opțional — implicit e-mailul firmei)</Label><Input value={form.executant_gdpr_email} onChange={(e) => upd("executant_gdpr_email", e.target.value)} placeholder="gdpr@firma.ro sau contact@firma.ro" /></div>
          </div>

          {/* Beneficiar (persoană vizată) */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-indigo-50 rounded-lg">
            <h3 className="col-span-2 font-semibold text-indigo-800">
              Persoană vizată (Beneficiar {isPF ? "— Persoană Fizică" : "— Persoană Juridică"})
            </h3>
            <div className="col-span-2">
              <Label>{isPF ? "Nume și prenume" : "Denumire firmă"}</Label>
              <Input value={form.beneficiar_name} onChange={(e) => upd("beneficiar_name", e.target.value)} data-testid="gdpr-beneficiar-name" />
            </div>
            {isPF ? (
              <>
                <div><Label>CNP</Label><Input value={form.beneficiar_cnp} onChange={(e) => upd("beneficiar_cnp", e.target.value)} placeholder="13 cifre" data-testid="gdpr-cnp" /></div>
                <div><Label>Serie/Nr. CI</Label><Input value={form.beneficiar_ci} onChange={(e) => upd("beneficiar_ci", e.target.value)} placeholder="XX 123456" /></div>
                <div className="col-span-2"><Label>Adresa de domiciliu</Label><Input value={form.beneficiar_address} onChange={(e) => upd("beneficiar_address", e.target.value)} /></div>
              </>
            ) : (
              <>
                <div><Label>CUI</Label><Input value={form.beneficiar_cui} onChange={(e) => upd("beneficiar_cui", e.target.value)} data-testid="gdpr-beneficiar-cui" /></div>
                <div><Label>Nr. Reg. Com.</Label><Input value={form.beneficiar_reg_com} onChange={(e) => upd("beneficiar_reg_com", e.target.value)} placeholder="J17/279/2020" /></div>
                <div className="col-span-2"><Label>Sediu social</Label><Input value={form.beneficiar_address} onChange={(e) => upd("beneficiar_address", e.target.value)} /></div>
                <div><Label>Reprezentant legal</Label><Input value={form.beneficiar_reprezentant} onChange={(e) => upd("beneficiar_reprezentant", e.target.value)} /></div>
                <div><Label>Funcție reprezentant</Label><Input value={form.beneficiar_functie} onChange={(e) => upd("beneficiar_functie", e.target.value)} placeholder="Administrator" /></div>
              </>
            )}
            <div><Label>Telefon</Label><Input value={form.beneficiar_phone} onChange={(e) => upd("beneficiar_phone", e.target.value)} /></div>
            <div><Label>E-mail</Label><Input value={form.beneficiar_email} onChange={(e) => upd("beneficiar_email", e.target.value)} /></div>
          </div>

          {/* Contract & scop */}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nr. contract</Label><Input value={form.contract_number} onChange={(e) => upd("contract_number", e.target.value)} /></div>
            <div><Label>Data contract</Label><Input type="date" value={form.contract_date} onChange={(e) => upd("contract_date", e.target.value)} /></div>
            <div className="col-span-2"><Label>Obiect lucrare</Label><Input value={form.obiect_lucrare} onChange={(e) => upd("obiect_lucrare", e.target.value)} /></div>
            <div className="col-span-2"><Label>Locație / punct de lucru</Label><Input value={form.locatie_lucrare} onChange={(e) => upd("locatie_lucrare", e.target.value)} /></div>
            <div><Label>Data acordului</Label><Input type="date" value={form.data_acord} onChange={(e) => upd("data_acord", e.target.value)} /></div>
            <div><Label>Perioadă stocare</Label><Input value={form.perioada_stocare} onChange={(e) => upd("perioada_stocare", e.target.value)} /></div>
          </div>

          {/* Scop suplimentar & clauze */}
          <div>
            <Label>Scop suplimentar de prelucrare (opțional)</Label>
            <Input value={form.scop_prelucrare_extra} onChange={(e) => upd("scop_prelucrare_extra", e.target.value)} placeholder="Ex: marketing, newsletter (necesită consimțământ separat)" />
          </div>
          <div>
            <Label>Clauze suplimentare (opțional)</Label>
            <Textarea value={form.clauze_suplimentare} onChange={(e) => upd("clauze_suplimentare", e.target.value)} rows={3} placeholder="Alte prevederi specifice — ex. transfer date către subcontractanți nominalizați, prelucrări speciale..." data-testid="gdpr-clauze" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-anexa-gdpr">
              <Save className="w-4 h-4 mr-2" />Salvează {existingAnnex?.annex_label || "ANEXA"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
